import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePermissions } from '.'
import {
  permissionActions,
  permissionNames,
  privacyTypes,
  urlList,
} from '../constants/constants'
import { apiService, dataStoreService, roleService } from '../services'
import { RootState } from '../store'
import {
  DataStore,
  setDataStoreDetailsCsv,
  setDataStoreList,
  setDataStoreListCsv,
  setDataStoreListUiFlags,
  setDataStoreLoading,
} from '../store/reducers/dataStoreReducer'
import useDownloadFile from './useDownloadFile'

interface UseDataStoresProps {
  preventFetch?: boolean
}

const useDataStores = ({ preventFetch = false }: UseDataStoresProps) => {
  const dispatch = useDispatch()
  const loading = useSelector((state: RootState) => state.dataStore.loading)
  const currentUser = useSelector((state: RootState) => state.user.currentUser)
  const downloadCsv = useDownloadFile()
  const {
    selected: selectedDataStore,
    dataStoreList: rawDataStoreList,
    uiFlags: dataStoreListUiFlag,
  } = useSelector((state: RootState) => state.dataStore)

  const selectedDataStoreUiFlag = selectedDataStore?.uiFlags

  const {
    isDeletable: isSelectedDataStoreDeletable,
    isUpdatable: isSelectedDataStoreUpdatable,
    isCreatable: isSelectedDataStoreCreatable,
  } = usePermissions({
    uiFlag: selectedDataStoreUiFlag ?? 0,
  })

  const { isDeletable, isUpdatable, isCreatable } = usePermissions({
    uiFlag: dataStoreListUiFlag ?? 0,
  })

  const dataStoreList: DataStore[] = useMemo(
    () =>
      rawDataStoreList
        .filter((dataStore: DataStore) =>
          currentUser &&
          roleService.hasPermission(
            currentUser,
            permissionNames.DATA_STORES,
            permissionActions.ALL
          )
            ? true
            : dataStore.visibility !== privacyTypes.PRIVATE
        )
        .map((dataStore: DataStore) => {
          return {
            ...dataStore,
            key: dataStore.id === undefined ? dataStore.id : dataStore.id,
          }
        }),
    [currentUser, rawDataStoreList]
  )

  const fetchDataStoreList = useCallback(
    (refreshForceCall = false) => {
      if (!loading && (!preventFetch || refreshForceCall)) {
        dispatch(setDataStoreLoading(true))
        apiService
          .fetchItems(urlList.DATA_STORES)
          .then((response: any) => {
            dispatch(setDataStoreLoading(false))
            dispatch(setDataStoreList(response.data))
            dispatch(setDataStoreListUiFlags(response.uiFlags))
          })
          .catch((error) => {
            console.error('axios fetch error', error)
            dispatch(setDataStoreLoading(false))
          })
      }
    },
    [dispatch, loading, preventFetch]
  )

  const refreshDataStoreList = useCallback(async () => {
    fetchDataStoreList(true)
  }, [fetchDataStoreList])

  useEffect(() => {
    fetchDataStoreList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exportDataStoreList = useCallback(() => {
    if (dataStoreList) {
      dataStoreService
        .exportDataStoreList()
        .then((response) => {
          const fileName = 'dataStores.csv'
          downloadCsv(response, fileName)
          dispatch(setDataStoreListCsv(response))
        })
        .catch((error: any) => {
          console.error('Error fetching data source list CSV data', error)
        })
    }
  }, [dataStoreList, dispatch, downloadCsv])

  const exportDataStoreDetailsCsv = useCallback(
    (dataStoreId: string) => {
      if (!selectedDataStore || dataStoreId === undefined || dataStoreId === '')
        return

      dataStoreService
        .exportDataStoreDetails({ dataStoreId })
        .then((response) => {
          const fileName = `${selectedDataStore?.title}_${selectedDataStore?.createdDate}.csv`
          downloadCsv(response, fileName)
          dispatch(setDataStoreDetailsCsv(response))
        })
        .catch((error: any) => {
          console.error('Error fetching data source details CSV data', error)
        })
    },
    [downloadCsv, selectedDataStore, dispatch]
  )

  interface GetPermittedUsersOfDataStoreProps {
    dataStoreId: string
  }

  const getPermittedUsersOfDataStore = useCallback(
    async ({ dataStoreId }: GetPermittedUsersOfDataStoreProps) => {
      return dataStoreService.getPermittedUsersOfDataStore({ dataStoreId })
    },
    []
  )

  const fetchDataStoreDetails = useCallback(async (dataStoreId: string) => {
    try {
      const response = await dataStoreService.getDataStoreDetails({
        dataStoreId,
      })
      return response
    } catch (error) {
      console.error('axios fetch error', error)
      throw error
    }
  }, [])

  return {
    dataStoreList,
    loading,
    refreshDataStoreList,
    selectedDataStore,
    exportDataStoreDetailsCsv,
    exportDataStoreList,
    getPermittedUsersOfDataStore,
    fetchDataStoreDetails,
    isSelectedDataStoreDeletable,
    isSelectedDataStoreUpdatable,
    isSelectedDataStoreCreatable,
    isDeletable,
    isUpdatable,
    isCreatable,
  }
}

export default useDataStores
